import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  DollarSign, FileText, Clock, AlertTriangle, CheckCircle,
  Plus, RefreshCw, TrendingUp, Send
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BillingManager = () => {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [outstanding, setOutstanding] = useState(null);
  const [showDepositForm, setShowDepositForm] = useState(false);
  const [depositForm, setDepositForm] = useState({
    amount: '',
    percentage: '',
    description: 'Project Deposit'
  });

  useEffect(() => {
    loadProjects();
    loadOutstanding();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOutstanding = async () => {
    try {
      const response = await axios.get(`${API}/billing/outstanding`);
      setOutstanding(response.data);
    } catch (error) {
      console.error('Failed to load outstanding:', error);
    }
  };

  const loadProjectBilling = async (projectId) => {
    try {
      const response = await axios.get(`${API}/billing/project/${projectId}`);
      setBillingData(response.data);
      setSelectedProject(projectId);
    } catch (error) {
      console.error('Failed to load billing:', error);
      toast.error('Failed to load billing data');
    }
  };

  const handleCreateDeposit = async () => {
    if (!selectedProject) return;
    
    try {
      const payload = {
        project_id: selectedProject,
        amount: parseFloat(depositForm.amount) || 0,
        percentage: depositForm.percentage ? parseFloat(depositForm.percentage) : null,
        description: depositForm.description
      };
      
      await axios.post(`${API}/billing/deposit`, payload);
      toast.success('Deposit invoice created');
      setShowDepositForm(false);
      setDepositForm({ amount: '', percentage: '', description: 'Project Deposit' });
      loadProjectBilling(selectedProject);
      loadOutstanding();
    } catch (error) {
      console.error('Failed to create deposit:', error);
      toast.error('Failed to create deposit invoice');
    }
  };

  const handleCreateMilestoneInvoice = async (milestoneId) => {
    if (!selectedProject) return;
    
    try {
      await axios.post(`${API}/billing/milestone/${selectedProject}/${milestoneId}`);
      toast.success('Milestone invoice created');
      loadProjectBilling(selectedProject);
      loadOutstanding();
    } catch (error) {
      console.error('Failed to create invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <RefreshCw className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="billing-manager">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2">
          Billing Manager
        </h1>
        <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
          Milestone Billing & Deposit Automation
        </p>
      </div>

      {/* Outstanding Summary */}
      {outstanding && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase text-muted-foreground">Outstanding</p>
                  <p className="text-2xl font-bold font-mono text-yellow-500">
                    {formatCurrency(outstanding.total_outstanding)}
                  </p>
                </div>
                <DollarSign className="text-yellow-500" size={24} />
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase text-muted-foreground">Overdue</p>
                  <p className="text-2xl font-bold font-mono text-red-500">
                    {formatCurrency(outstanding.total_overdue)}
                  </p>
                </div>
                <AlertTriangle className="text-red-500" size={24} />
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase text-muted-foreground">Pending Invoices</p>
                  <p className="text-2xl font-bold font-mono">{outstanding.count}</p>
                </div>
                <FileText className="text-muted-foreground" size={24} />
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase text-muted-foreground">Overdue Count</p>
                  <p className="text-2xl font-bold font-mono text-red-500">{outstanding.overdue_count}</p>
                </div>
                <Clock className="text-red-500" size={24} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Project List */}
        <Card className="border-border lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg font-bold uppercase tracking-tight">
              Projects
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {projects.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No projects found</p>
              ) : (
                projects.map((project) => (
                  <button
                    key={project.id}
                    onClick={() => loadProjectBilling(project.id)}
                    className={`w-full text-left p-3 rounded-sm border transition-colors ${
                      selectedProject === project.id 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-muted-foreground/30'
                    }`}
                  >
                    <p className="font-medium text-sm">{project.project_name}</p>
                    <p className="text-xs text-muted-foreground">{project.client_name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-[10px]">
                        {project.status}
                      </Badge>
                      <span className="text-xs font-mono text-green-500">
                        {formatCurrency(project.quote_total)}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Billing Details */}
        <div className="lg:col-span-2 space-y-6">
          {billingData ? (
            <>
              {/* Billing Summary */}
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-bold uppercase tracking-tight">
                    {billingData.project_name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Quote Total</p>
                      <p className="text-lg font-bold font-mono">{formatCurrency(billingData.quote_total)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Invoiced</p>
                      <p className="text-lg font-bold font-mono text-blue-500">{formatCurrency(billingData.total_invoiced)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Paid</p>
                      <p className="text-lg font-bold font-mono text-green-500">{formatCurrency(billingData.total_paid)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Remaining</p>
                      <p className="text-lg font-bold font-mono text-yellow-500">{formatCurrency(billingData.remaining_to_bill)}</p>
                    </div>
                  </div>

                  {/* Progress Bars */}
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Billing Progress</span>
                        <span className="font-mono">{billingData.billing_progress}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="h-2 rounded-full bg-blue-500"
                          style={{ width: `${billingData.billing_progress}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Payment Progress</span>
                        <span className="font-mono">{billingData.payment_progress}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="h-2 rounded-full bg-green-500"
                          style={{ width: `${billingData.payment_progress}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-4">
                    <Button 
                      onClick={() => setShowDepositForm(!showDepositForm)}
                      variant="outline"
                      size="sm"
                      className="font-mono uppercase text-xs"
                    >
                      <Plus size={14} className="mr-1" />
                      Create Deposit
                    </Button>
                  </div>

                  {/* Deposit Form */}
                  {showDepositForm && (
                    <div className="mt-4 p-4 border border-border rounded-sm bg-muted/30">
                      <p className="text-sm font-semibold mb-3">Create Deposit Invoice</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Amount ($)</Label>
                          <Input
                            type="number"
                            value={depositForm.amount}
                            onChange={(e) => setDepositForm({...depositForm, amount: e.target.value})}
                            placeholder="0.00"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Or Percentage (%)</Label>
                          <Input
                            type="number"
                            value={depositForm.percentage}
                            onChange={(e) => setDepositForm({...depositForm, percentage: e.target.value})}
                            placeholder="e.g., 25"
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div className="mt-3">
                        <Label className="text-xs">Description</Label>
                        <Input
                          value={depositForm.description}
                          onChange={(e) => setDepositForm({...depositForm, description: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button onClick={handleCreateDeposit} size="sm">
                          Create Invoice
                        </Button>
                        <Button onClick={() => setShowDepositForm(false)} variant="outline" size="sm">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Milestone Billing */}
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-bold uppercase tracking-tight">
                    Milestone Billing
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {billingData.milestone_billing.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No milestones found</p>
                  ) : (
                    <div className="space-y-3">
                      {billingData.milestone_billing.map((ms, idx) => (
                        <div 
                          key={idx}
                          className="p-3 border border-border rounded-sm flex items-center justify-between"
                        >
                          <div>
                            <p className="font-medium text-sm">{ms.milestone_name}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <Badge variant="outline" className="text-[10px]">{ms.status}</Badge>
                              <span className="text-xs text-muted-foreground">
                                Invoiced: {formatCurrency(ms.invoiced)}
                              </span>
                              <span className="text-xs text-green-500">
                                Paid: {formatCurrency(ms.paid)}
                              </span>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleCreateMilestoneInvoice(ms.milestone_id)}
                            disabled={ms.invoiced > 0}
                            size="sm"
                            variant={ms.invoiced > 0 ? "ghost" : "outline"}
                            className="font-mono uppercase text-xs"
                          >
                            {ms.invoiced > 0 ? (
                              <><CheckCircle size={14} className="mr-1 text-green-500" /> Invoiced</>
                            ) : (
                              <><Send size={14} className="mr-1" /> Invoice</>
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Invoices List */}
              {billingData.invoices.length > 0 && (
                <Card className="border-border">
                  <CardHeader>
                    <CardTitle className="text-lg font-bold uppercase tracking-tight">
                      Project Invoices
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {billingData.invoices.map((inv, idx) => (
                        <div 
                          key={idx}
                          className="p-3 border border-border rounded-sm flex items-center justify-between"
                        >
                          <div>
                            <p className="font-mono text-sm">{inv.invoice_number}</p>
                            <p className="text-xs text-muted-foreground">
                              {inv.invoice_type} • Due: {new Date(inv.due_date).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold font-mono">{formatCurrency(inv.total)}</p>
                            <Badge 
                              className={
                                inv.status === 'Paid' ? 'bg-green-500/10 text-green-500' :
                                inv.status === 'Overdue' ? 'bg-red-500/10 text-red-500' :
                                'bg-yellow-500/10 text-yellow-500'
                              }
                            >
                              {inv.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="border-border">
              <CardContent className="p-8 text-center">
                <DollarSign className="mx-auto mb-4 text-muted-foreground/30" size={48} />
                <p className="text-muted-foreground">Select a project to view billing details</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingManager;
